/**
 * Residency bookkeeping for the Arena intensity-remix **families** (PRD #624 /
 * issue #626). A "family" is one arena run song's decoded `.mp3` remix set
 * (`<base>_2.._5`) - the single biggest decoded-audio cost in the game (several
 * minutes of stereo PCM per family), and before this slice every family a session
 * ever entered stayed decoded for the life of the tab.
 *
 * The policy: keep at most the **active** family (the current/most recent run's
 * song) plus a small bounded warm set of {@link ARENA_REMIX_WARM_FAMILIES}
 * recently-played families (a re-entered run reuses its settled decode), and
 * release everything older - but **never a family the live playback source still
 * references**: a playing family is exempt from `excess()` and is released at the
 * next prune after playback moves off it (the {@link ./musicPlayer.MusicPlayer}
 * re-prunes whenever the mp3 source changes, ends, or stops).
 *
 * Pure and deterministic (an ordered most-recent-first list, no clocks), the
 * {@link ./soundResidency.SoundResidency} twin at family granularity; the
 * MusicPlayer shell owns the actual buffer maps and drops them for the families
 * this class says are excess.
 */

/**
 * How many recently-played remix families stay decoded **beyond the active one**.
 * One: a player bouncing between two runs re-enters the previous song without a
 * re-download, while a long session touring the whole {@link ../sim/content.ARENA_PLAYLIST}
 * plateaus at two resident families instead of six.
 */
export const ARENA_REMIX_WARM_FAMILIES = 1;

/** The maximum resident remix families: the active one + the warm set. */
export const ARENA_REMIX_FAMILY_LIMIT = 1 + ARENA_REMIX_WARM_FAMILIES;

/** The base song (family) a remix track id belongs to (`combat1_3` -> `combat1`). */
export function remixFamilyOf(remixId: string): string {
  return remixId.replace(/_\d+$/, '');
}

export class RemixFamilyResidency {
  // Resident families, most recently used first.
  private order: string[] = [];
  private readonly limit: number;

  constructor(limit: number = ARENA_REMIX_FAMILY_LIMIT) {
    this.limit = Math.max(1, limit);
  }

  /** Mark `base` the most recently used (entering/looping a run on its song). */
  touch(base: string): void {
    this.order = [base, ...this.order.filter((b) => b !== base)];
  }

  /**
   * The families beyond the keep limit that are safe to release right now -
   * everything past the limit **except** `playingFamily` (a family the live
   * source still references is deferred until playback moves off it).
   */
  excess(playingFamily?: string): string[] {
    return this.order.slice(this.limit).filter((base) => base !== playingFamily);
  }

  /** Forget a released family (its next use is a fresh load + touch). */
  drop(base: string): void {
    this.order = this.order.filter((b) => b !== base);
  }

  /** The resident families, most recently used first (tests/metrics). */
  resident(): readonly string[] {
    return this.order;
  }

  /** Forget everything (dispose). */
  clear(): void {
    this.order = [];
  }
}
